import { useState } from "react";
import { useMutation, useReactiveVar } from "@apollo/client";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  authenticationStateVar,
  resetAuthenticationState,
  setAuthenticationState,
} from "@/apollo/auth-state";
import { apolloClient } from "@/apollo/client";
import { env } from "@/lib/env";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  GET_DRAFT_PREVIEW,
  PUBLISH_PREVIEW,
  type PublishPreviewData,
  type PublishPreviewVariables,
} from "@/graphql";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const ADD_INSTAGRAM_OPTION = "__add_instagram__";

const navLinks = [
  { to: "/build", label: "Build" },
  { to: "/design", label: "Design" },
  { to: "/integrate", label: "Integrate" },
  { to: "/preview", label: "Preview" },
] as const;

export function Header() {
  const authState = useReactiveVar(authenticationStateVar);
  const navigate = useNavigate();
  const [publishing, setPublishing] = useState(false);

  const [publishPreview] = useMutation<
    PublishPreviewData,
    PublishPreviewVariables
  >(PUBLISH_PREVIEW);

  const { authenticated, accounts, selectedAccount } = authState;
  const showAccountControls = authenticated && accounts.length > 0;

  async function handlePublish() {
    if (publishing || !selectedAccount) return;
    setPublishing(true);
    try {
      await publishPreview({
        variables: { instagramUsername: selectedAccount },
        refetchQueries: [
          {
            query: GET_DRAFT_PREVIEW,
            variables: { instagramUsername: selectedAccount },
          },
        ],
      });
      analytics.track("preview.publish", {
        instagramUsername: selectedAccount,
      });

      // Third-party tracking events (HubSpot, Intercom, Custify)
      const w = window as Window & {
        _hsq?: { push: (event: unknown[]) => void };
        Intercom?: (action: string, event: string) => void;
        _ctrack?: { track: (name: string) => void };
      };
      w._hsq?.push(["trackEvent", { id: "Published Sked Link" }]);
      w.Intercom?.("trackEvent", "published-sked-link");
      w._ctrack?.track("published-sked-link");

      toast.success("Published!");
    } catch (error) {
      console.error("publish failed", error);
      toast.error("There was an issue publishing, please try again later.");
    } finally {
      setPublishing(false);
    }
  }

  async function handleCopyLink() {
    if (!selectedAccount) return;
    const url = `${env.skedLinkPublicDomain}/${selectedAccount}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.info("Copied to clipboard");
    } catch (error) {
      console.error("clipboard write failed", error);
      toast.error("Failed to copy to clipboard");
    }
  }

  function handleAccountChange(value: string) {
    if (value === ADD_INSTAGRAM_OPTION) {
      navigate("/create");
      return;
    }
    setAuthenticationState({ selectedAccount: value });
  }

  async function handleSignOut() {
    try {
      await fetch(`${env.skedLinkBackendUrl}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Authentication sign-out failed", error);
    }
    resetAuthenticationState();
    await apolloClient.clearStore().catch(() => undefined);
    navigate("/login");
  }

  return (
    <header className="border-b border-neutral-100 bg-white px-4">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-3 py-3">
        <div className="flex items-center gap-4">
          <Link
            to={showAccountControls ? "/build" : "/login"}
            className="font-gotham text-lg font-semibold text-neutral-900"
          >
            Sked Link
          </Link>

          {showAccountControls ? (
            <nav className="flex items-center gap-1 rounded-lg border border-neutral-100 bg-neutral-25 p-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      "rounded-md px-3 py-2 font-inter text-sm font-medium text-neutral-600 transition-colors hover:text-neutral-900",
                      isActive &&
                        "bg-white text-blue-600 shadow-sm ring-1 ring-neutral-100"
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          ) : null}
        </div>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          {showAccountControls ? (
            <>
              <Select
                value={selectedAccount}
                onValueChange={handleAccountChange}
              >
                <SelectTrigger className="w-[180px] bg-white">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account} value={account}>
                      {account}
                    </SelectItem>
                  ))}
                  <SelectItem value={ADD_INSTAGRAM_OPTION}>
                    Add Instagram
                  </SelectItem>
                </SelectContent>
              </Select>

              <Button type="button" onClick={handleCopyLink} variant="outline">
                <i className="fa-regular fa-copy" />
                Copy link
              </Button>

              <Button
                type="button"
                onClick={handlePublish}
                disabled={publishing}
                variant="default"
              >
                {publishing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-cloud-arrow-up" />
                    Publish
                  </>
                )}
              </Button>
            </>
          ) : null}

          {authenticated ? (
            <Button onClick={handleSignOut} variant="ghost">
              Logout
            </Button>
          ) : (
            <Button asChild variant="ghost">
              <Link to="/login">Login</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
